import { motion } from 'framer-motion';
import { Eye, EyeOff, Fingerprint, Terminal } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import validator from 'validator';
import api from '../config/axiosConfig';
import { useAuth } from '../contexts/AuthContext';

// ── Grille de fond ────────────────────────────────────────────────────────────
const PerspectiveGrid = () => (
  <div
    className="absolute inset-0 pointer-events-none"
    style={{
      backgroundImage: `
        linear-gradient(to right, rgba(34,211,238,0.06) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(245,158,11,0.06) 1px, transparent 1px)
      `,
      backgroundSize: '80px 80px',
      maskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 100%)',
      WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 100%)',
    }}
  />
);

// ── Barre de scan animée ──────────────────────────────────────────────────────
const ScanLine = () => (
  <motion.div
    animate={{ top: ['-2%', '102%'] }}
    transition={{ duration: 5, repeat: Infinity, ease: 'linear', repeatDelay: 1.5 }}
    style={{
      position: 'absolute',
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.4) 30%, rgba(245,158,11,0.5) 70%, transparent 100%)',
      boxShadow: '0 0 12px rgba(34,211,238,0.2)',
      zIndex: 10,
      pointerEvents: 'none',
    }}
  />
);

// ── Champ de saisie natif (sans MUI) ─────────────────────────────────────────
const HudInput = ({ label, value, onChange, type = 'text', error, icon: Icon, rightSlot }) => {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <label
        style={{
          fontFamily: 'monospace',
          fontSize: '0.52rem',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: focused ? '#22d3ee' : 'rgba(255,255,255,0.65)',
          transition: 'color 0.2s',
        }}
      >
        {label}
      </label>
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          background: focused
            ? 'rgba(34,211,238,0.03)'
            : 'rgba(255,255,255,0.02)',
          border: `1px solid ${
            error
              ? 'rgba(239,68,68,0.5)'
              : focused
              ? 'rgba(34,211,238,0.3)'
              : 'rgba(255,255,255,0.06)'
          }`,
          transition: 'border-color 0.2s, background 0.2s',
          padding: '0 0.75rem',
        }}
      >
        {/* Corner accent – top left */}
        <span
          style={{
            position: 'absolute',
            top: -1,
            left: -1,
            width: 6,
            height: 6,
            borderTop: `1px solid ${focused ? '#22d3ee' : 'rgba(34,211,238,0.2)'}`,
            borderLeft: `1px solid ${focused ? '#22d3ee' : 'rgba(34,211,238,0.2)'}`,
            transition: 'border-color 0.2s',
          }}
        />
        <span
          style={{
            position: 'absolute',
            bottom: -1,
            right: -1,
            width: 6,
            height: 6,
            borderBottom: `1px solid ${focused ? '#f59e0b' : 'rgba(245,158,11,0.2)'}`,
            borderRight: `1px solid ${focused ? '#f59e0b' : 'rgba(245,158,11,0.2)'}`,
            transition: 'border-color 0.2s',
          }}
        />

        {Icon && (
          <Icon
            size={13}
            style={{
              color: focused ? '#22d3ee' : 'rgba(255, 255, 255, 0.88)',
              flexShrink: 0,
              marginRight: '0.6rem',
              transition: 'color 0.2s',
            }}
          />
        )}

        <input
          type={type}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            letterSpacing: '0.08em',
            color: '#e2e8f0',
            padding: '0.85rem 0',
            caretColor: '#22d3ee',
          }}
        />

        {rightSlot && (
          <div style={{ flexShrink: 0, marginLeft: '0.5rem' }}>{rightSlot}</div>
        )}
      </div>
      {error && (
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '0.48rem',
            letterSpacing: '0.25em',
            color: 'rgba(239,68,68,0.8)',
            textTransform: 'uppercase',
          }}
        >
          ▲ {error}
        </span>
      )}
    </div>
  );
};

// ── Écran de vérification du rôle ─────────────────────────────────────────────
const RoleResolvingScreen = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem', zIndex: 30 }}>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
      style={{ width: 80, height: 80, borderRadius: '50%', border: '1px solid rgba(34,211,238,0.15)', borderTop: '1px solid #22d3ee', borderRight: '1px solid rgba(245,158,11,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 30px rgba(34,211,238,0.08), inset 0 0 20px rgba(245,158,11,0.04)' }}
    >
      <img src="/logo2026.png" alt="PESA" style={{ width: 28, objectFit: 'contain', filter: 'brightness(1.3)' }} />
    </motion.div>
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#22d3ee', marginBottom: '0.5rem' }}>Authority_Verification</p>
      <p style={{ fontFamily: 'monospace', fontSize: '0.52rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)' }}>Rôle &amp; accréditation en cours...</p>
    </div>
    <div style={{ width: 200, height: 1, background: 'rgba(255,255,255,0.06)', position: 'relative', overflow: 'hidden' }}>
      <motion.div animate={{ x: ['-100%', '100%'] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, #22d3ee, #f59e0b, transparent)' }} />
    </div>
    <p style={{ fontFamily: 'monospace', fontSize: '0.44rem', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.15)', textTransform: 'uppercase' }}>Ne pas fermer la page</p>
  </div>
);

export const getRedirectPath = (role) => {
  const r = role?.toLowerCase();
  if (r === 'superadmin') return '/back-office';
  if (['admin', 'administrateur', 'entreprise', 'collectivité', 'établissement', 'staff'].includes(r)) return '/admin';
  return '/dashboard';
};

// ── Composant principal ───────────────────────────────────────────────────────
const Login = () => {
  const { login, isAuthenticated, loading, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResolvingRole, setIsResolvingRole] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // ── Redirection après auth ────────────────────────────────────────────────
  useEffect(() => {
    // Attendre que AuthContext ait fini (loading===false) ET que user soit là
    if (loading || !isAuthenticated || !user) return;
    if (hasRedirected.current) return;
 
    let isCancelled = false;
 
    const resolveAndRedirect = async () => {
      setIsResolvingRole(true);
      hasRedirected.current = true;
 
      try {
        const plan = user.subscription?.plan;
        const needsSubscriptionCheck = plan && plan !== 'freemium';
        const pendingPlan = location.state?.plan;

        if (pendingPlan) {
          try {
            await api.patch('/users/me/plan', { plan: pendingPlan });
          } catch (err) {
            console.error('Erreur application plan:', err);
          }
        }
 
        if (needsSubscriptionCheck) {
          // Seul cas où on a besoin d'un appel réseau : vérifier incomplete
          const subRes = await api.get('/subscription/status');
          const { subscription, subscriptionPlan } = subRes.data;
 
          if (!isCancelled && subscription?.status === 'incomplete') {
            toast.warn(`Accréditation ${subscriptionPlan} en attente.`, { position: 'top-center' });
            navigate(`/subscribe/${subscriptionPlan}`, { replace: true });
            return;
          }
        }
 
        // Redirection basée sur user.role déjà dans AuthContext — zéro latence
        if (!isCancelled) {
          navigate(getRedirectPath(user.role), { replace: true });
        }
      } catch (err) {
        console.error('Erreur résolution rôle/subscription', err);
        if (!isCancelled) {
          toast.error('Impossible de vérifier votre rôle. Redirection par défaut.');
          navigate(getRedirectPath(user.role), { replace: true });
        }
      } finally {
        if (!isCancelled) setIsResolvingRole(false);
      }
    };
 
    resolveAndRedirect();
    return () => { isCancelled = true; };
  }, [loading, isAuthenticated, user, navigate]);

  // ── Soumission du formulaire ──────────────────────────────────────────────
  const handleLogin = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validator.isEmail(email)) return setEmailError('Format email invalide');
      if (password.length < 1) return setPasswordError('Mot de passe requis');
      try {
        await login(email, password);
      } catch (err) {
        if (err?.response?.data?.code === 'Email non vérifié') {
          navigate('/register-success', {
            state: { email }
          });
          return;
        }
        toast.error('Échec de connexion — vérifiez vos identifiants.');
      }
    },
    [email, password, login, navigate]
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#020617',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'monospace',
      }}
    >
      {/* ── Fonds atmosphériques ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 30% 20%, rgba(34,211,238,0.04) 0%, transparent 40%), radial-gradient(circle at 70% 80%, rgba(245,158,11,0.04) 0%, transparent 40%)',
        }}
      />
      <PerspectiveGrid />

      {/* ── Grain ── */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />

      {/* ── Contenu principal ── */}
      {isResolvingRole ? (
        <RoleResolvingScreen />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          style={{ width: '100%', maxWidth: 420, padding: '0 1.5rem', zIndex: 20 }}
        >
          {/* ── Panneau principal ── */}
          <div
            style={{
              position: 'relative',
              background: 'rgba(2,6,23,0.7)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            {/* Accent top */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.5) 30%, rgba(245,158,11,0.5) 70%, transparent 100%)',
              }}
            />

            <ScanLine />

            {/* Coins HUD */}
            <span
              style={{
                position: 'absolute',
                top: -1,
                left: -1,
                width: 12,
                height: 12,
                borderTop: '2px solid #22d3ee',
                borderLeft: '2px solid #22d3ee',
              }}
            />
            <span
              style={{
                position: 'absolute',
                bottom: -1,
                right: -1,
                width: 12,
                height: 12,
                borderBottom: '2px solid #f59e0b',
                borderRight: '2px solid #f59e0b',
              }}
            />

            <div style={{ padding: '2.5rem 2rem 2rem' }}>

              {/* ── En-tête ── */}
              <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                {/* Logo carré HUD */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 52,
                    height: 52,
                    border: '1px solid rgba(245,158,11,0.2)',
                    background: 'rgba(245,158,11,0.04)',
                    position: 'relative',
                    marginBottom: '1.25rem',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: -1,
                      left: -1,
                      width: 8,
                      height: 8,
                      borderTop: '2px solid #22d3ee',
                      borderLeft: '2px solid #22d3ee',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      bottom: -1,
                      right: -1,
                      width: 8,
                      height: 8,
                      borderBottom: '2px solid #f59e0b',
                      borderRight: '2px solid #f59e0b',
                    }}
                  />
                <img src="/logo512.png" alt="P" style={{ width: 22, objectFit: 'contain', filter: 'brightness(1.2)' }} />
                </div>

                {/* Nom de la plateforme */}
                <div
                  style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: '#fff',
                    lineHeight: 1.1,
                    marginBottom: '0.35rem',
                  }}
                >
                  PESA
                  <span
                    style={{
                      background: 'linear-gradient(90deg, #22d3ee, #f59e0b)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    CORE
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.91)', fontSize: '0.7rem' }}>
                    {' '}·{' '}AUTH
                  </span>
                </div>

                {/* Indicateur de statut */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.4rem',
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: '#10b981',
                      boxShadow: '0 0 6px #10b981',
                      animation: 'pulse 2s ease-in-out infinite',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '0.44rem',
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: 'rgba(255, 255, 255, 0.8)',
                    }}
                  >
                    Statut: opérationnel
                  </span>
                </div>
              </div>

              {/* ── Formulaire ── */}
              <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <HudInput
                  label="Identification · Email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                  type="email"
                  error={emailError}
                  icon={Terminal}
                />

                <HudInput
                  label="Accès · Mot de passe"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                  type={showPassword ? 'text' : 'password'}
                  error={passwordError}
                  icon={Fingerprint}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.2rem',
                        color: 'rgba(255, 255, 255, 0.88)',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'color 0.2s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#22d3ee')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.88)')}
                      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    >
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  }
                />

                {/* CTA principal */}
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={!loading ? { boxShadow: '0 0 30px rgba(245,158,11,0.35)' } : {}}
                  whileTap={!loading ? { scale: 0.98 } : {}}
                  style={{
                    marginTop: '0.5rem',
                    width: '100%',
                    padding: '1rem',
                    background: loading
                      ? 'rgba(255,255,255,0.04)'
                      : 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)',
                    border: loading ? '1px solid rgba(255,255,255,0.06)' : 'none',
                    color: loading ? '#dfdfdf' : '#020617',
                    fontFamily: 'monospace',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'background 0.3s, color 0.3s',
                  }}
                >
                  {loading ? 'Authentification...' : 'Se connecter'}
                </motion.button>
              </form>

              {/* ── Liens secondaires ── */}
              <div
                style={{
                  marginTop: '2rem',
                  paddingTop: '1.5rem',
                  borderTop: '1px solid rgba(255, 255, 255, 0.58)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.85rem',
                }}
              >
                <button
    type="button"
    onClick={() => {
    const target = email && validator.isEmail(email)
      ? `/forgot-password?email=${encodeURIComponent(email)}`
      : '/forgot-password';
    window.location.href = target;   // ← force la mise à jour de l'URL
  }}
    style={{
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'monospace',
      fontSize: '0.5rem',
      letterSpacing: '0.28em',
      textTransform: 'uppercase',
      color: 'rgba(255, 255, 255, 0.88)',
      transition: 'color 0.2s',
      padding: '0.25rem',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
    onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.88)')}
  >
    [ Mot de passe oublié? ]
  </button>

                <button
                  type="button"
                  onClick={() => navigate('/auth/register')}
                  style={{
                    background: 'none',
                    border: '1px solid rgba(245,158,11,0.15)',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'rgba(245,158,11,0.6)',
                    padding: '0.6rem 1.5rem',
                    transition: 'all 0.2s',
                    width: '100%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#f59e0b';
                    e.currentTarget.style.borderColor = 'rgba(245,158,11,0.4)';
                    e.currentTarget.style.background = 'rgba(245,158,11,0.04)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'rgba(245,158,11,0.6)';
                    e.currentTarget.style.borderColor = 'rgba(245,158,11,0.15)';
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  Pas de compte? Inscrivez-vous →
                </button>
              </div>
            </div>
          </div>

          {/* ── Métadonnées bas de panneau ── */}
          <div
            style={{
              marginTop: '1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              opacity: 0.35,
            }}
          >
            <span style={{ fontSize: '0.62rem', letterSpacing: '0.22em', color: '#dfdfdf', textTransform: 'uppercase' }}>
              Pesacore Architect 2026
            </span>
            <span style={{ fontSize: '0.62rem', letterSpacing: '0.22em', color: '#dfdfdf', textTransform: 'uppercase' }}>
              © Tous droits réservés
            </span>
          </div>
        </motion.div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default Login;